import type {
  AiRepairOperations,
  DeterministicRepairValidation,
  HrDataIssues,
} from './types';
import type { buildRepairSummary } from './normalizeRepairOperations';

const sampleDuplicateEvidence = (operations: AiRepairOperations, limit = 3) =>
  operations.duplicateMerges.slice(0, limit).map(item => ({
    secondTimestamp: item.secondTimestamp,
    sourceCount: item.sourceTimestamps.length,
    mergedHr: item.merged.hr,
  }));

const sampleGapEvidence = (operations: AiRepairOperations, limit = 3) =>
  operations.gapInsertions.slice(0, limit).map(item => ({
    timestamp: item.timestamp,
    fromTimestamp: item.fromTimestamp,
    toTimestamp: item.toTimestamp,
    hr: item.hr,
  }));

export const buildRepairSummaryPrompt = (
  issues: HrDataIssues,
  operations: AiRepairOperations,
  deterministic: DeterministicRepairValidation,
  summary: ReturnType<typeof buildRepairSummary>
) => {
  const systemPrompt = `Sei un revisore di riparazioni dati fisiologici HR/HRV/LF/HF.
La riparazione numerica è già stata eseguita in locale con metodi attesi:
- duplicati nello stesso secondo → media aritmetica
- gap temporali → interpolazione lineare tra i confini del gap

Devi produrre una sintesi breve in italiano, comprensibile per un ingegnere biomedico.
Non inventare problemi non supportati dall'evidence.
Se deterministicPassed è true e countsMatchExpected è true, lo status deve essere "verified".

Rispondi SOLO con JSON valido:
{
  "status": "verified" | "rejected",
  "summary": "stringa breve in italiano",
  "findings": ["punto 1", "punto 2"]
}`;

  const userPrompt = `Esito controlli locali:
- deterministicPassed: ${deterministic.passed}
- countsMatchExpected: ${summary.countsMatchExpected}
- checks falliti: ${
    deterministic.checks
      .filter(check => !check.passed)
      .map(check => check.message)
      .join(' | ') || 'nessuno'
  }

Conteggi riparazione (JSON compatto):
${JSON.stringify(
  {
    duplicateIssues: issues.duplicates.length,
    gapIssues: issues.gaps.length,
    duplicateMergeOperations: summary.duplicateMergeOperations,
    gapInsertionOperations: summary.gapInsertionOperations,
    correctionsCount: summary.correctionsCount,
    rawOriginalEventCount: summary.rawOriginalEventCount,
    uniqueOriginalSeconds: summary.uniqueOriginalSeconds,
    expectedRepairedCount: summary.expectedRepairedCount,
    actualRepairedCount: summary.actualRepairedCount,
    countFormula: summary.countFormula,
    duplicateEvidenceSample: sampleDuplicateEvidence(operations),
    gapEvidenceSample: sampleGapEvidence(operations),
  },
  null,
  2
)}

Produci summary e findings in italiano.`;

  return [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ] as Array<{ role: string; content: string }>;
};
